import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Loading,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { marketsForScope } from '../dataProvider/types';
import type {
  AdminDataProvider,
  BlockSchedules,
  Market,
  PageSchema,
} from '../dataProvider';
import {
  PAGE_ID_HELP,
  PAGE_ID_PATTERN,
  invalidBlockTypes,
  parseBlock,
  type DraftBlock,
} from './pageSchemaModel';
import { BLOCK_TYPE_LABEL } from './pageSchemaModel';
import type { Block } from './blocks';
import { useCatalog } from './useCatalog';
import { BlockComposer } from './BlockComposer';
import { PreviewPane } from './PreviewPane';
import { MarketScheduleCalendar } from './MarketScheduleCalendar';
import { PublishPanel } from './PublishPanel';

/**
 * The page-schema authoring form (D-page-schema-editor-8a). DRAFTS ONLY: it authors
 * a (pageId, market) draft of blocks. There is NO publish, NO version bump and NO
 * go-live here (those are sub-surface 8b); a draft saves with status 'draft',
 * version 0 and publishedAt null (the contract version / publishedAt are populated
 * at publish in 8b). A draft saves ONLY if every block is VALID against the mirrored
 * BlockSchema (the authoring-time guard); an invalid block blocks the save. Gated
 * pages:author at editor and operational market-scoped (a uk editor authors uk
 * schemas). All draft mutations run through the audited provider.
 */

const toDraftBlocks = (blocks: Block[]): DraftBlock[] =>
  blocks.map((b) => ({ type: b.type, id: b.id, data: { ...b.data } }));

const PageSchemaFormInner = ({ record }: { record?: PageSchema }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const { identity } = useGetIdentity();
  const { activeRegion } = useRegion();
  const catalog = useCatalog();

  const isEdit = record != null;
  const markets = useMemo(
    () => marketsForScope(activeRegion ?? 'GLOBAL'),
    [activeRegion],
  );

  const [pageId, setPageId] = useState<string>(record?.pageId ?? 'home');
  const [market, setMarket] = useState<Market>(
    record?.market ?? markets[0] ?? 'uk',
  );
  const [blocks, setBlocks] = useState<DraftBlock[]>(
    record ? toDraftBlocks(record.blocks) : [],
  );
  // The per-block schedules (8d) live ALONGSIDE the blocks, keyed by block id.
  const [schedules, setSchedules] = useState<BlockSchedules>(
    record?.blockSchedules ?? {},
  );
  const [showPreview, setShowPreview] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  // A stable clock for the composer's derived schedule chips and the calendar.
  const [now] = useState<number>(() => Date.now());

  const save = async () => {
    if (!PAGE_ID_PATTERN.test(pageId)) {
      notify(`Page id must be one of ${PAGE_ID_HELP}`, { type: 'error' });
      return;
    }
    // A draft saves ONLY if every block is valid against the contract (the publish
    // GATE over the whole schema is 8b; this is the per-block authoring guard).
    const invalid = invalidBlockTypes(blocks);
    if (invalid.length > 0) {
      notify(
        `Finish these blocks before saving: ${invalid
          .map((t) => BLOCK_TYPE_LABEL[t])
          .join(', ')}`,
        { type: 'error' },
      );
      return;
    }
    const parsed: Block[] = blocks.map((b) => parseBlock(b).data as Block);
    const nowIso = new Date().toISOString();

    try {
      if (isEdit && record) {
        await dataProvider.update('pageSchemas', {
          id: record.id,
          data: {
            pageId,
            market,
            blocks: parsed,
            blockSchedules: schedules,
            updatedAt: nowIso,
          },
          previousData: record,
        });
        notify('Draft saved', { type: 'success' });
        redirect('list', 'pageSchemas');
      } else {
        const { data } = await dataProvider.create('pageSchemas', {
          data: {
            pageId,
            market,
            // Authored in DRAFT; version / publishedAt / versions are set at publish (8b).
            status: 'draft',
            version: 0,
            publishedAt: null,
            versions: [],
            blocks: parsed,
            blockSchedules: schedules,
            createdBy: String(identity?.email ?? identity?.id ?? 'unknown'),
            createdAt: nowIso,
            updatedAt: nowIso,
          },
        });
        notify('Draft created', { type: 'success' });
        redirect('edit', 'pageSchemas', data.id);
      }
    } catch {
      notify('Could not save the draft', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 880 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEdit ? 'Edit page draft' : 'New page draft'}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Page id"
              value={pageId}
              onChange={(e) => setPageId(e.target.value)}
              helperText={PAGE_ID_HELP}
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'page-id' }}
            />
            <TextField
              select
              label="Market"
              value={market}
              onChange={(e) => setMarket(e.target.value as Market)}
              margin="dense"
              size="small"
              sx={{ minWidth: 140 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'page-market' }}
            >
              {markets.map((m) => (
                <option key={m} value={m}>
                  {m.toUpperCase()}
                </option>
              ))}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <BlockComposer
            blocks={blocks}
            onChange={setBlocks}
            catalog={catalog}
            schedules={schedules}
            onSchedulesChange={setSchedules}
            now={now}
          />
        </CardContent>
      </Card>

      {/* The live preview (8c) is READ-ONLY and operates on the SAVED draft; save
          first to preview pending composer edits. It mounts only on edit (it needs a
          saved row to read draft and published facets and the per-market siblings). */}
      {isEdit && record && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => setShowPreview((v) => !v)}
              data-testid="toggle-preview"
            >
              {showPreview ? 'Hide preview' : 'Show preview'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowCalendar((v) => !v)}
              data-testid="toggle-calendar"
            >
              {showCalendar
                ? 'Hide schedule calendar'
                : 'Show schedule calendar'}
            </Button>
          </Stack>
        </Box>
      )}
      {isEdit && record && showPreview && <PreviewPane record={record} />}
      {/* The market-wide schedule calendar (8d): every scheduled draft block across
          the in-scope market's pages, operational-scoped via marketsForScope. */}
      {isEdit && record && showCalendar && (
        <MarketScheduleCalendar markets={markets} now={now} />
      )}

      {/* The publish lifecycle (8b) operates on the SAVED draft; save first, then
          publish. The composer above is unchanged from 8a. */}
      {isEdit && record && <PublishPanel record={record} />}

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={save} data-testid="save-draft">
          {isEdit ? 'Save draft' : 'Create draft'}
        </Button>
        <Button onClick={() => redirect('list', 'pageSchemas')}>Cancel</Button>
      </Stack>
    </Box>
  );
};

/** Create view. Exported for testing without the access wrapper. */
export const PageSchemaCreateView = () => <PageSchemaFormInner />;

/** Edit view. Exported for testing without the access wrapper. */
export const PageSchemaEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<PageSchema>('pageSchemas', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return <PageSchemaFormInner record={record} />;
};

export const PageSchemaCreate = () => (
  <RequireAccess capability="pages:author">
    <PageSchemaCreateView />
  </RequireAccess>
);

export const PageSchemaEdit = () => (
  <RequireAccess capability="pages:author">
    <PageSchemaEditView />
  </RequireAccess>
);
